import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { UnderlineTabs, type UnderlineTabOption } from "@/components/ui/underline-tabs";

import { statusFilterOptionValues, type ProjectStatusFilterValue } from "./projectStatus";

interface ProjectStatusFilterProps {
  value: ProjectStatusFilterValue;
  onValueChange: (value: ProjectStatusFilterValue) => void;
  counts: { active: number; inactive: number; all: number };
  /** Offer the "All" option. The project picker's two tabs omit it. */
  includeAll?: boolean;
  /** The options to offer, in display order — overrides `includeAll`. The
   *  dashboard leads with All and drops a status no project has. */
  options?: readonly ProjectStatusFilterValue[];
  /**
   * "tabs" (default): the app's underline tab strip, for list pickers.
   * "inline": a compact segmented control that sits on a form field's label
   * row, so a Select keeps its height.
   */
  variant?: "tabs" | "inline";
  className?: string;
}

/**
 * Active · Inactive (· All) filter for a project choice. Inside a Select's
 * dropdown, mount it through {@link ProjectSelectContent}, which adds the
 * keyboard path the dropdown's focus trap otherwise denies it.
 */
export function ProjectStatusFilter({
  value,
  onValueChange,
  counts,
  includeAll = true,
  options: optionValues,
  variant = "tabs",
  className,
}: ProjectStatusFilterProps) {
  const { t } = useLanguage();

  // An option with nothing in it can't be picked. The chosen one stays
  // enabled even at 0: the default can land on an empty option (every project
  // inactive opens on Active), and it must still read as the selection.
  const options: UnderlineTabOption<ProjectStatusFilterValue>[] = (
    optionValues ?? statusFilterOptionValues(includeAll)
  ).map((option) => ({
    value: option,
    label: t.projects.status[option],
    count: counts[option],
    disabled: counts[option] === 0 && option !== value,
  }));

  if (variant === "inline") {
    return (
      <div
        role="group"
        aria-label={t.projects.status.filterLabel}
        className={cn("inline-flex items-center gap-0.5", className)}
      >
        {options.map((option) => {
          const isActive = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isActive}
              disabled={option.disabled}
              onClick={() => onValueChange(option.value)}
              className={cn(
                "rounded border px-2 py-0.5 text-xs transition-colors",
                isActive
                  ? "border-foreground/40 font-medium text-foreground"
                  : "border-transparent text-muted-foreground",
                option.disabled
                  ? "cursor-not-allowed opacity-50"
                  : !isActive && "hover:text-foreground"
              )}
            >
              {option.label} <span className="tabular-nums opacity-70">{option.count}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // UnderlineTabs relies on its host's bottom border (see there), so the
  // wrapper draws it.
  return (
    <div
      role="group"
      aria-label={t.projects.status.filterLabel}
      className={cn("border-b", className)}
    >
      <UnderlineTabs
        value={value}
        onValueChange={onValueChange}
        options={options}
        className="-mb-px"
      />
    </div>
  );
}
